import { Component, Injector, Input, OnChanges, OnInit, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AbstractComponent, AttendanceCopilotApiService, SettingsModuleService } from 'lib-trend-core';
import { takeUntil } from 'rxjs/operators';
import { CopilotModelSelectDialogComponent } from '../copilot-model-select-dialog/copilot-model-select-dialog.component';
import { CopilotModelOption, modelLabel, providerLabel, providerLogo, providerSlugOf } from '../copilot-model-select-dialog/copilot-model.util';

@Component({
  selector: 'channel-config-copilot',
  templateUrl: './channel-config-copilot.component.html',
  styleUrls: ['./channel-config-copilot.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChannelConfigCopilotComponent extends AbstractComponent implements OnInit, OnChanges {
  @Input() channel: any;

  settingsModule: any = null;
  saving = false;
  uploading = false;

  activeCopilotModule = false;
  copilotLanguage = 'pt-BR';
  copilotTraining = '';
  copilotPlaybook = '';
  copilotPlaybookFileUrl = '';
  copilotModel = '';
  copilotMinMessages = 20;

  models: CopilotModelOption[] = [];
  loadingModels = false;
  modelsError: string | null = null;

  readonly LANGUAGES = [
    { value: 'pt-BR', label: 'Português (Brasil)' },
    { value: 'en-US', label: 'English (US)' },
    { value: 'es',    label: 'Español' },
  ];

  readonly MAX_TRAINING = 5000;
  readonly MAX_PLAYBOOK = 20000;

  constructor(
    injector: Injector,
    private readonly settingsModuleService: SettingsModuleService,
    private readonly copilotApiService: AttendanceCopilotApiService,
    private readonly dialog: MatDialog,
  ) {
    super(injector);
  }

  /** The selected model, while the catalog still offers it. */
  private get selectedModel(): CopilotModelOption | undefined {
    return this.models.find((m) => m.id === this.copilotModel);
  }

  /** Friendly name of the currently selected model (falls back to the raw id). */
  get selectedModelName(): string {
    if (!this.copilotModel) return '';
    const model = this.selectedModel;
    return model ? modelLabel(model) : this.copilotModel;
  }

  /** A free model shows the Free label instead of its creator (RF-53); one the gateway withdrew falls back. */
  get selectedModelFree(): boolean {
    return this.selectedModel?.free === true;
  }

  /** Company label (OpenAI, Anthropic, ...) of the currently selected model. */
  get selectedProviderLabel(): string {
    if (!this.copilotModel) return '';
    return this.selectedModelFree
      ? this.t('channel.copilotModelSelectDialog.freeProvider')
      : providerLabel(providerSlugOf(this.copilotModel));
  }

  /** Logo asset path of the currently selected model's provider. */
  get selectedProviderLogo(): string {
    return this.copilotModel ? providerLogo(providerSlugOf(this.copilotModel)) : '';
  }

  /** Tracks whether the selected provider logo failed to load (show placeholder). */
  selectedLogoFailed = false;

  openModelDialog(): void {
    if (this.loadingModels || this.modelsError) return;
    this.dialog
      .open(CopilotModelSelectDialogComponent, {
        width: '760px',
        maxWidth: '95vw',
        data: { models: this.models, selectedId: this.copilotModel },
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((modelId: string | undefined) => {
        if (modelId) {
          this.copilotModel = modelId;
          this.selectedLogoFailed = false;
        }
      });
  }

  /** Clamps the minimum-messages input to a non-negative integer (default 20). */
  private coerceMinMessages(value: unknown): number {
    const n = Math.floor(Number(value));
    return Number.isFinite(n) && n >= 0 ? n : 20;
  }

  ngOnInit(): void {
    this.loadModels();
  }

  private loadModels(): void {
    this.loadingModels = true;
    this.modelsError = null;
    this.copilotApiService
      .listModels()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.models = res?.data ?? [];
          this.modelsError = res?.error ?? (this.models.length ? null : this.t('channel.channelConfigCopilot.noModelsAvailable'));
          this.loadingModels = false;
        },
        error: (err) => {
          this.modelsError = this.t('channel.channelConfigCopilot.loadModelsError');
          this.loadingModels = false;
          console.error('Failed to load copilot models', err);
        },
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['channel']?.currentValue) {
      this.load();
    }
  }

  private load(): void {
    this.loading = true;
    this.settingsModuleService
      .getByChannel(this.channel._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (s: any) => {
          this.settingsModule = s;
          this.activeCopilotModule   = s?.activeCopilotModule   ?? false;
          this.copilotLanguage       = s?.copilotLanguage       ?? 'pt-BR';
          this.copilotTraining       = s?.copilotTraining       ?? '';
          this.copilotPlaybook       = s?.copilotPlaybook       ?? '';
          this.copilotPlaybookFileUrl = s?.copilotPlaybookFileUrl ?? '';
          this.copilotModel          = s?.copilotModel          ?? '';
          this.copilotMinMessages    = s?.copilotMinMessages    ?? 20;
          this.selectedLogoFailed    = false;
          this.loading = false;
        },
        error: () => { this.loading = false; },
      });
  }

  save(): void {
    if (!this.settingsModule) return;
    // A model must be selected to enable the copilot — there is no default.
    if (this.activeCopilotModule && !this.copilotModel) {
      this.alertService.warning(this.t('channel.channelConfigCopilot.selectModelWarning'));
      return;
    }
    this.saving = true;
    const patch = {
      ...this.settingsModule,
      activeCopilotModule:    this.activeCopilotModule,
      copilotLanguage:        this.copilotLanguage,
      copilotTraining:        this.copilotTraining,
      copilotPlaybook:        this.copilotPlaybook,
      copilotPlaybookFileUrl: this.copilotPlaybookFileUrl,
      copilotModel:           this.copilotModel,
      copilotMinMessages:     this.coerceMinMessages(this.copilotMinMessages),
    };
    this.settingsModuleService
      .update(this.settingsModule._id, patch)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.saving = false;
          this.alertService.success(this.t('channel.channelConfigCopilot.settingsSavedSuccess'));
        },
        error: () => { this.saving = false; },
      });
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      alert(this.t('channel.channelConfigCopilot.fileTooLarge'));
      input.value = '';
      return;
    }
    const allowed = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowed.includes(file.type)) {
      alert(this.t('channel.channelConfigCopilot.unsupportedFormat'));
      input.value = '';
      return;
    }

    this.uploading = true;

    this.settingsModuleService
      .uploadCopilotPlaybook(file)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.copilotPlaybook        = res.text;
          this.copilotPlaybookFileUrl = res.url;
          this.uploading = false;
          input.value = '';
        },
        error: () => {
          alert(this.t('channel.channelConfigCopilot.uploadFailed'));
          this.uploading = false;
          input.value = '';
        },
      });
  }
}
